import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fromJS, List, Map } from 'immutable';
import { isEqual } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ancestorHasClass } from '../../../../utils/helpers/dom.helper';
import { merger } from '../../../../utils/helpers/model.helper';
import { DIALOG_CLASS } from '../../../dialog/dialog.data';
import {
  DocumentTypesEnum,
  MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION,
} from '../../../document-data/document-data.data';
import { Document as DocumentModel } from '../../../document-data/document.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { ItemCalculationResult } from '../../../document-data/sub-models/item-calculation-result.model';
import { Reference } from '../../../document-data/sub-models/reference.model';
import { CONTENT_KEY } from '../../../entity-data/entity.model';
import { SELECT_OPTION_CLASS } from '../../../form-master/components/select-field/select-field.component';
import { ItemDataBuilder } from '../../../item-data/item-data.builder';
import { ItemUnitTypesEnum } from '../../../item-data/item-data.data';
import { Item } from '../../../item-data/item.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { AUTOCOMPLETE_CLASS } from '../../../shared/directives/auto-complete/auto-complete.directive';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
} from '../../../shared/directives/auto-complete/auto-complete.interface';
import { CalculatorService } from '../../../shared/services/calculator/calculator.service';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import {
  IDocumentItemOptionsInfo,
  IMarkupInfo,
} from '../document-item-options/document-item-options.component';
import { AddDocumentItemsBuilder } from './add-document-items.builder';
import { takeUntil } from 'rxjs/operators';
import { IDocumentDataBuilder } from '../../../document-data/document-data-builder.interface';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { IFormFieldErrorMessages } from '../../../form-master/form-master.interface';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { ItemSaveService } from '../../../shared/services/item-save/item-save.service';

@Component({
  selector: 'app-add-document-items',
  templateUrl: 'add-document-items.component.html',
  styleUrls: ['add-document-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AddDocumentItemsBuilder, ItemDataBuilder],
})
export class AddDocumentItemsComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() documentItem: DocumentItem;
  @Input() document: DocumentModel;
  @Input() documentTaxes: List<Tax>;
  @Input() documentWithholdingTax: WithholdingTax;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() companySettings: CompanySettings;
  @Input() enableDiscount: boolean;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output()
  deleteDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  saveDocumentItem: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output()
  saveItemToList: EventEmitter<DocumentItem> = new EventEmitter<DocumentItem>();
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() chooseMultiple: EventEmitter<void> = new EventEmitter<void>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _el: any;
  private _emptyForm: any;
  private _emptyFormStream: Subject<boolean>;
  private _editingDocumentItem: BehaviorSubject<DocumentItem>;
  private _reference: Reference;
  private _markupInfo: IMarkupInfo;
  private _disableAutoTax: boolean;
  private _autoListTrackingObject: ITrackingObject;
  private _itemUnitType: ItemUnitTypesEnum;
  private _handleClickOutsideCallback = this._handleClickOutside.bind(this);

  productCodeFocusableTrigger: Subject<void> = new Subject<void>();
  nameFocusableTrigger: Subject<void> = new Subject<void>();
  descriptionFocusableTrigger: Subject<void> = new Subject<void>();
  editingDocumentItem$: Observable<DocumentItem>;
  emptyForm$: Observable<boolean>;
  item: DocumentItem;
  formKeys: any;
  form: FormGroup;
  itemTotal: number;
  itemSaveAction: ITrackingAction;
  itemDeleteAction: ITrackingAction;
  itemCancelAction: ITrackingAction;
  autoCompleteOptions: any;
  productCodeFocusableTrigger$: Observable<void>;
  nameFocusableTrigger$: Observable<void>;
  descriptionFocusableTrigger$: Observable<void>;
  fetchingResults: { [formKey: string]: boolean };
  loadingSpinnerIcon: IIcon;
  descriptionMaxlength: number;
  itemNameErrorMessage: IFormFieldErrorMessages;
  hasItemSaveAccess: boolean;
  primaryButtonTextKey = 'ACTION.SAVE';

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _addDocumentItemsBuilder: AddDocumentItemsBuilder,
    private _elementRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef,
    private _calculatorService: CalculatorService,
    private _loggerService: LoggerService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _featuresDataStore: FeaturesDataStore,
    private _itemSaveService: ItemSaveService
  ) {}

  ngOnInit() {
    const item: DocumentItem =
      this.documentItem ||
      this.documentDataBuilder.buildDocumentItem(
        {},
        this.documentTaxes,
        this.documentWithholdingTax
      );

    this._itemUnitType = item.unitType;
    this._el = this._elementRef.nativeElement;
    this._autoListTrackingObject = this._trackingObjectBuilder.buildAutoList();

    this.descriptionMaxlength = MAX_LENGTH_DOCUMENT_ITEM_DESCRIPTION;
    this.fetchingResults = {};
    this.formKeys = this._addDocumentItemsBuilder.FORM_KEYS;
    this.item = item;
    this.form = this._addDocumentItemsBuilder.buildForm(
      this.item,
      this.companySettings.productNameEnabled
    );
    this.itemNameErrorMessage = this._addDocumentItemsBuilder.buildItemNameErrorMessages(
      this.companySettings.productNameEnabled
    );
    this.loadingSpinnerIcon = {
      name: 'loading-spinner',
      size: IconSizesEnum.medium,
    };
    this.productCodeFocusableTrigger$ = this.productCodeFocusableTrigger.asObservable();
    this.nameFocusableTrigger$ = this.nameFocusableTrigger.asObservable();
    this.descriptionFocusableTrigger$ = this.descriptionFocusableTrigger.asObservable();
    this.itemSaveAction = this._trackingActionBuilder.buildButtonTapped(
      'save_item',
      this.trackingScreenName
    );
    this.itemCancelAction = this._trackingActionBuilder.buildButtonTapped(
      'cancel_item',
      this.trackingScreenName
    );
    this.itemDeleteAction = this._trackingActionBuilder.buildButtonTapped(
      'delete_item',
      this.trackingScreenName
    );

    this._reference = this.item.reference;
    this._emptyForm = this._addDocumentItemsBuilder.buildForm(
      this.documentDataBuilder.buildDocumentItem(
        {},
        this.documentTaxes,
        this.documentWithholdingTax
      ),
      this.companySettings.productNameEnabled
    ).value;
    this._editingDocumentItem = new BehaviorSubject<DocumentItem>(this.item);
    this._emptyFormStream = new BehaviorSubject<boolean>(this.isFormEmpty());

    this.editingDocumentItem$ = this._editingDocumentItem.asObservable();
    this.emptyForm$ = this._emptyFormStream.asObservable();

    this.recalculateAutoCompleteOptions();

    this.editingDocumentItem$
      .pipe(takeUntil(this._destroyed))
      .subscribe((documentItem: DocumentItem) => {
        this._disableAutoTax = false;

        this.itemTotal = this.calculateDocumentItemTotal(documentItem);

        this._changeDetectorRef.markForCheck();
      });

    this._featuresDataStore
      .getFeaturePayloads()
      .pipe(takeUntil(this._destroyed))
      .subscribe((payloads) => {
        this.hasItemSaveAccess = this._itemSaveService.hasItemSaveV1Enabled(
          payloads
        );

        if (this.hasItemSaveAccess) {
          this.primaryButtonTextKey = this.documentItem
            ? 'ACTION.UPDATE'
            : 'ACTION.ADD';
        }
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.documentSettings.showProductCode) {
        this.productCodeFocusableTrigger.next();
      } else if (this.companySettings.productNameEnabled) {
        this.nameFocusableTrigger.next();
      } else {
        this.descriptionFocusableTrigger.next();
      }

      this._document.addEventListener(
        'click',
        this._handleClickOutsideCallback
      );
    }, 50);
  }

  recalculateAutoCompleteOptions() {
    this.autoCompleteOptions =
      this.documentItem && this.documentItem.referenceId
        ? {}
        : this._addDocumentItemsBuilder.buildAutoCompleteOptions(
            this._editingDocumentItem.value,
            this.documentSettings.showProductCode,
            this.documentSettings.separatePartsAndLabor,
            this.companySettings.productNameEnabled,
            () => this.chooseMultiple.emit()
          );

    this._changeDetectorRef.markForCheck();
  }

  onAutoCompleteFetchStart(formKey: string) {
    this.fetchingResults[formKey] = true;
  }

  onAutoCompleteFetchEnd({ list, query }: IAutoCompleteOnFetchEnd) {
    this.clearSearchLoadingSpinner();

    if (list && query) {
      this._trackingDispatcher.trackEvent(
        this._autoListTrackingObject,
        this._trackingActionBuilder.buildSearched(
          query,
          list.size,
          this.trackingScreenName
        )
      );
    }
  }

  clearSearchLoadingSpinner() {
    this.fetchingResults = {};

    this._changeDetectorRef.markForCheck();
  }

  onUpdateDocumentItem(documentItemInfo: Map<string, any>) {
    this._loggerService.log(
      'on update document item:',
      documentItemInfo.toJS()
    );

    const documentItemProps = this._addDocumentItemsBuilder.buildDocumentItemProps(
      documentItemInfo.set(DocumentItem.KEYS.UNIT_TYPE, this._itemUnitType),
      this.companySettings.productNameEnabled,
      this._reference,
      this._markupInfo,
      this._disableAutoTax
    );

    this.updateDocumentItem(documentItemProps);
  }

  onUpdateDocumentItemOptions(
    documentItemOptionsInfo: IDocumentItemOptionsInfo
  ) {
    this._loggerService.log('on update document item options');

    this._markupInfo = documentItemOptionsInfo.markup;
    this._itemUnitType = documentItemOptionsInfo.documentItemProps.get(
      DocumentItem.KEYS.UNIT_TYPE
    );

    const documentItemProps = this._addDocumentItemsBuilder.buildDocumentItemPropsFromDocumentItemOptionsInfo(
      fromJS(this.form.value),
      documentItemOptionsInfo
    );

    this.updateDocumentItem(
      merger(documentItemOptionsInfo.documentItemProps, documentItemProps)
    );
  }

  updateDocumentItem(documentItemProps: Map<string, any>) {
    const newDocumentItem = merger(
      this._editingDocumentItem.value,
      documentItemProps
    );
    const documentItem = this.documentDataBuilder.buildDocumentItem(
      newDocumentItem,
      this.documentTaxes,
      this.documentWithholdingTax,
      this._disableAutoTax
    );

    this._editingDocumentItem.next(documentItem);

    this.checkEmptyForm();
  }

  calculateDocumentItemTotal(documentItem: DocumentItem): number {
    const tempDoc = this.document.setIn(
      [CONTENT_KEY, DocumentModel.CONTENT_KEYS.ITEMS],
      List<DocumentItem>([documentItem])
    );
    const tempDocCalculation: CalculationResult = new CalculationResult(
      this._calculatorService
        .getCalculations(tempDoc as DocumentModel)
        .get(DocumentModel.KEYS.LATEST_CALCULATION_RESULTS)
    );
    const itemCalculation: ItemCalculationResult = tempDocCalculation.items.first();

    return itemCalculation ? itemCalculation.subTotal : 0;
  }

  checkFormValidity() {
    const formControl = this.form.get(
      this.companySettings.productNameEnabled
        ? this.formKeys.NAME
        : this.formKeys.DESCRIPTION
    );

    // trigger error message if need be
    formControl.markAsDirty();
    formControl.updateValueAndValidity();
  }

  onSaveDocumentItem() {
    this.checkFormValidity();

    if (this.form.valid) {
      const documentItem: DocumentItem = this._editingDocumentItem.value;
      this.saveDocumentItem.emit(documentItem);

      this._saveItemToItemList(documentItem);
    }
  }

  checkEmptyForm() {
    this._emptyFormStream.next(this.isFormEmpty());
  }

  isFormEmpty() {
    return (
      isEqual(this.form.value, this._emptyForm) &&
      this.item.equals(this._editingDocumentItem.value)
    );
  }

  onAutoCompleteSet(
    { value }: IAutoCompleteOnSet,
    nextFieldFocusableTrigger?: Subject<void>
  ) {
    this._loggerService.log('autocomplete add document item select:', value.id);

    const item: Item = value;

    if (item.id) {
      const onlySelf = { onlySelf: true };
      const form = this.form;
      const formKeys = this.formKeys;
      const productCodeControl = form.get(formKeys.CODE);
      const nameControl = form.get(formKeys.NAME);
      const quantityControl = form.get(formKeys.QTY);
      const itemName = item.name;
      const itemDetails = item.details;
      const code = item.code;
      const productNameEnabled = this.companySettings.productNameEnabled;
      const unitPriceValue: number =
        this.document.documentType === DocumentTypesEnum.purchase_order
          ? item.cost || item.rate || 0
          : item.rate || 0;

      this._trackingDispatcher.trackEvent(
        this._autoListTrackingObject,
        this._trackingActionBuilder.buildTapped(this.trackingScreenName)
      );

      this._reference = this._addDocumentItemsBuilder.buildReference(item);

      if (nameControl && nameControl.enabled) {
        if (productNameEnabled) {
          nameControl.setValue(itemName, onlySelf);
          nameControl.disable(onlySelf);
        }

        if (!itemName) {
          nameControl.setErrors({ required: true });
        }
      }

      this._itemUnitType = item.unitType;
      this._disableAutoTax = !item.taxable;

      productCodeControl.setValue(code, onlySelf);

      form
        .get(formKeys.DESCRIPTION)
        .setValue(productNameEnabled ? itemDetails : itemName, onlySelf);
      quantityControl.setValue(quantityControl.value || 1, onlySelf);
      form.get(formKeys.UNIT_PRICE).setValue(unitPriceValue, onlySelf);

      form.updateValueAndValidity();
      form.markAsDirty();
    } else {
      this._trackingDispatcher.trackEvent(
        this._autoListTrackingObject,
        this._trackingActionBuilder.buildButtonTapped(
          'select_new_item',
          this.trackingScreenName
        )
      );

      this._reference = null;
    }

    // TODO: refactor to use streams
    // setTimeout so that the form changes can propagate before these operations
    setTimeout(() => {
      this.item = this._editingDocumentItem.value;
      this.recalculateAutoCompleteOptions();

      if (nextFieldFocusableTrigger) {
        nextFieldFocusableTrigger.next();
      }
    }, 50);
  }

  ngOnDestroy() {
    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _handleClickOutside(evt: Event) {
    const evtTarget = evt.target;
    const dialogOverlay = this._document.querySelector(DIALOG_CLASS);

    if (!evtTarget) {
      return;
    }

    if (
      !(dialogOverlay && dialogOverlay.contains(evtTarget as any)) &&
      !this._el.contains(evtTarget) &&
      !ancestorHasClass(evtTarget, SELECT_OPTION_CLASS) &&
      !ancestorHasClass(evtTarget, AUTOCOMPLETE_CLASS)
    ) {
      this.onSaveDocumentItem();
    }
  }

  private _saveItemToItemList(documentItem: DocumentItem) {
    const hasItemBeenSavedToItemList = !!documentItem.referenceId;

    if (hasItemBeenSavedToItemList) {
      return;
    }

    if (!this.hasItemSaveAccess) {
      return this.saveItemToList.emit(documentItem);
    }

    const isItemSaveEnabledInSettings = this.companySettings.saveItems;

    if (isItemSaveEnabledInSettings) {
      return this.saveItemToList.emit(documentItem);
    }
  }
}
